import { escapeHtml, formatNtp } from './format.js';
import { dictionaries } from './i18n.js';

export function renderDailyChart(items = [], language = 'ru', metric = 'count', { width = 760 } = {}) {
  const isAmount = metric === 'amount';
  const labels = dictionaries[language] || dictionaries.ru;
  const height = 320;
  const rows = items.map((item) => ({
    date: String(item.date || ''),
    amount: String(item.amount ?? '0'),
    value: isAmount ? amountUnits(item.amount) : countUnits(item.count),
  }));

  if (!rows.length) {
    return `<div class="chart-empty">${escapeHtml(labels['common.empty'])}</div>`;
  }

  const peak = rows.reduce((max, row) => row.value > max ? row.value : max, 0n);
  const tickStep = niceStep(peak);
  const max = tickStep * 4n > peak ? tickStep * 4n : ((peak + tickStep - 1n) / tickStep) * tickStep;
  const ticks = [];
  for (let value = 0n; value <= max; value += tickStep) ticks.push(value);
  const integerFormat = new Intl.NumberFormat(language);
  const axisLabel = (value) => isAmount ? amountAxisLabel(value, language) : integerFormat.format(value);
  const margin = { top: 22, right: 24, bottom: 58, left: Math.min(108, Math.max(40, ...ticks.map((value) => axisLabel(value).length * 7 + 16))) };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const step = rows.length > 1 ? plotWidth / (rows.length - 1) : 0;
  const pointX = (index) => rows.length > 1 ? margin.left + step * index : margin.left + plotWidth / 2;
  const pointY = (value) => margin.top + plotHeight - (Number(value) / Number(max)) * plotHeight;
  const points = rows.map((row, index) => ({
    ...row,
    x: pointX(index),
    y: pointY(row.value),
  }));

  const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${fixed(point.x)} ${fixed(point.y)}`).join(' ');
  const area = `${path} L ${fixed(points.at(-1).x)} ${fixed(margin.top + plotHeight)} L ${fixed(points[0].x)} ${fixed(margin.top + plotHeight)} Z`;
  const grid = ticks.map((tick) => {
    const y = pointY(tick);
    return `
      <line class="chart-grid" x1="${margin.left}" y1="${fixed(y)}" x2="${width - margin.right}" y2="${fixed(y)}" />
      <text class="chart-y-label" x="${margin.left - 10}" y="${fixed(y + 4)}" text-anchor="end">${escapeHtml(axisLabel(tick))}</text>
    `;
  }).join('');
  const xLabels = xLabelIndexes(rows.length, Math.max(2, Math.floor(plotWidth / 80))).map((index) => {
    const point = points[index];
    return `
      <text class="chart-x-label" x="${fixed(point.x)}" y="${height - 20}" text-anchor="middle">${escapeHtml(formatShortDate(point.date, language))}</text>
    `;
  }).join('');
  const hoverLayers = points.map((point, index) => {
    const previous = index === 0 ? margin.left : (points[index - 1].x + point.x) / 2;
    const next = index === points.length - 1 ? width - margin.right : (point.x + points[index + 1].x) / 2;
    const hitX = previous;
    const hitWidth = Math.max(10, next - previous);
    const dateLabel = formatShortDate(point.date, language, { long: true });
    const valueLabel = isAmount ? formatNtp(point.amount) : integerFormat.format(point.value);
    const tooltipWidth = Math.max(94, Math.min(plotWidth, Math.max(dateLabel.length, valueLabel.length) * 9 + 24));
    const tooltipFontSize = Math.min(14, (tooltipWidth - 24) / (valueLabel.length * 0.62));
    const tooltipHeight = 44;
    const tooltipX = clamp(point.x - tooltipWidth / 2, margin.left, width - margin.right - tooltipWidth);
    const tooltipY = point.y > margin.top + tooltipHeight + 12 ? point.y - tooltipHeight - 12 : point.y + 14;
    return `
      <g class="chart-hover-target" tabindex="0" aria-label="${escapeHtml(`${dateLabel}: ${valueLabel}`)}">
        <rect class="chart-hitarea" x="${fixed(hitX)}" y="${margin.top}" width="${fixed(hitWidth)}" height="${plotHeight}" />
        <line class="chart-hover-line" x1="${fixed(point.x)}" y1="${margin.top}" x2="${fixed(point.x)}" y2="${margin.top + plotHeight}" />
        <circle class="chart-point" cx="${fixed(point.x)}" cy="${fixed(point.y)}" r="3.2" />
        <circle class="chart-hover-point" cx="${fixed(point.x)}" cy="${fixed(point.y)}" r="5.5" />
        <g class="chart-tooltip" aria-hidden="true">
          <rect x="${fixed(tooltipX)}" y="${fixed(tooltipY)}" width="${fixed(tooltipWidth)}" height="${tooltipHeight}" rx="7" />
          <text class="chart-tooltip-date" x="${fixed(tooltipX + 12)}" y="${fixed(tooltipY + 17)}">${escapeHtml(dateLabel)}</text>
          <text class="chart-tooltip-value" style="font-size: ${fixed(tooltipFontSize)}px" x="${fixed(tooltipX + 12)}" y="${fixed(tooltipY + 34)}">${escapeHtml(valueLabel)}</text>
        </g>
      </g>
    `;
  }).join('');

  return `
    <svg class="daily-chart" viewBox="0 0 ${width} ${height}" role="img" aria-label="${escapeHtml(labels[isAmount ? 'chart.amountTitle' : 'chart.countTitle'])}">
      <defs>
        <linearGradient id="dailyChartFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stop-color="currentColor" stop-opacity="0.18" />
          <stop offset="100%" stop-color="currentColor" stop-opacity="0.02" />
        </linearGradient>
      </defs>
      ${grid}
      <line class="chart-axis" x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${margin.top + plotHeight}" />
      <line class="chart-axis" x1="${margin.left}" y1="${margin.top + plotHeight}" x2="${width - margin.right}" y2="${margin.top + plotHeight}" />
      ${xLabels}
      <path class="chart-area" d="${area}" />
      <path class="chart-line" d="${path}" />
      ${hoverLayers}
    </svg>
  `;
}

function fixed(value) {
  return Number(value).toFixed(1);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function countUnits(value) {
  const raw = String(value ?? '0');
  return /^\d+$/.test(raw) ? BigInt(raw) : 0n;
}

function amountUnits(value) {
  const match = String(value ?? '0').match(/^(\d+)(?:\.(\d{1,9}))?$/);
  return match ? BigInt(match[1]) * 1000000000n + BigInt((match[2] || '').padEnd(9, '0')) : 0n;
}

function amountAxisLabel(units, language) {
  const whole = units / 1000000000n;
  if (whole >= 1000n) {
    return new Intl.NumberFormat(language, { notation: 'compact', maximumSignificantDigits: 3 }).format(whole);
  }
  const fraction = (units % 1000000000n).toString().padStart(9, '0').replace(/0+$/, '');
  return `${whole}${fraction ? `.${fraction}` : ''}`;
}

function niceStep(maxValue) {
  if (maxValue <= 4n) return 1n;
  const rough = (maxValue + 3n) / 4n;
  const base = 10n ** BigInt(rough.toString().length - 1);
  for (const factor of [1n, 2n, 5n, 10n]) {
    if (base * factor >= rough) return base * factor;
  }
}

function xLabelIndexes(length, segments) {
  if (length <= 1) return [0];
  const interval = Math.max(1, Math.ceil((length - 1) / segments));
  const indexes = new Set([0, length - 1]);
  for (let index = interval; index < length - 1; index += interval) {
    indexes.add(index);
  }
  return [...indexes].sort((a, b) => a - b);
}

function formatShortDate(value, language, { long = false } = {}) {
  if (!value) return '-';
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(language, {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    ...(long ? { year: 'numeric' } : {}),
  }).format(parsed);
}
